import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Check } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

import TestimonialsCarousel from '@/components/new-home-page/TestimonialsCarousel';
import { Link } from 'react-router-dom';

const Index: React.FC = () => {

  return (
    <div className="w-full min-w-0 flex flex-col min-h-screen bg-white font-sans overflow-x-hidden">

      <header className="fixed top-0 left-0 right-0 bg-white border-b border-[#E5E7EB] z-50">
        <div className="container mx-auto px-10 py-3 flex items-center justify-between">
          <a href="/" className="text-xl font-bold text-[#0070F3]">
            KeepMore
          </a>
          <div className="flex items-center gap-4">
           <Link to="/auth?mode=login" className="text-[#6B7280] hover:underline font-medium">
              Login
            </Link>
            <a 
              href="https://cal.com/michael-collins-foxtvy/30min" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-[#0070F3] hover:bg-[#0059CC] text-white px-6 py-2 rounded-md">
                Book a Demo
              </Button>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="w-full pt-40 pb-16 md:pt-48 md:pb-20 relative overflow-hidden">
        <div className="container mx-auto px-6 max-w-5xl relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            {/* Title */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1E1E1E] mb-8 leading-tight">
              <span className="text-[#0070F3]">KeepMore</span> of your revenue
            </h1>
            
            {/* Subtitle */}
            <p className="text-xl md:text-2xl text-[#6B7280] mb-16 max-w-3xl mx-auto leading-relaxed">
              Credit cards are killing your margins. Quick Stripe checkout pages that let customers choose ACH or cover the cost of using cards.
            </p>
            
            {/* Features List - Single Row */}
            <div className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2 mb-12">
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Check className="h-5 w-5 text-[#0070F3] flex-shrink-0" />
                <span className="text-sm text-[#000000]">Connects directly to Stripe</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Check className="h-5 w-5 text-[#0070F3] flex-shrink-0" />
                <span className="text-sm text-[#000000]">No changes to your setup</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Check className="h-5 w-5 text-[#0070F3] flex-shrink-0" />
                <span className="text-sm text-[#000000]">Five minute onboarding</span>
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <Check className="h-5 w-5 text-[#0070F3] flex-shrink-0" />
                <span className="text-sm text-[#000000] font-semibold">$39/mo • 30-day free trial</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col gap-4 justify-center items-center">
              {/* CTA Row - Button and Link aligned on same baseline */}
              <div className="flex flex-row gap-6 justify-center items-center">
                <Link to="/auth?mode=signup">
                  <Button 
                  className="bg-[#0070F3] hover:bg-[#0059CC] text-white px-8 py-4 rounded-md text-base font-semibold shadow-sm hover:shadow-md transition-all"
                >
                    Start Free Trial →
                  </Button>
                </Link>
                <a 
                  href="#how-it-works" 
                  className="text-[#0070F3] hover:text-[#0059CC] text-base font-medium flex items-center gap-1 group"
                >
                  See how it works
                  <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
              
              {/* Meta */}
              <div className="mt-3 flex flex-col items-center gap-3">
                <p className="text-xs text-muted-foreground">(no credit card required)</p>

                {/* Stripe Trust Badge */}
                <img
                  src="/powered-by-stripe.png"
                  alt="Powered by Stripe"
                  loading="lazy"
                  decoding="async"
                  className="h-8 sm:h-9 w-auto object-contain"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <TestimonialsCarousel />

      {/* New Customers Section */}
      <section id="how-it-works" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-4xl font-bold text-[#000000] mb-6 text-center">
              How it works
            </h2>
            <div className="text-lg text-[#6B7280] mb-12 text-center max-w-[700px] mx-auto">
              <p className="mb-2"><span className="text-[#0070F3] font-semibold">KeepMore</span> creates Stripe-hosted checkout pages for new customers.</p>
              <p>They see clear payment options and you keep your full amount without any extra setup.</p>
            </div>

            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
              {/* Steps - Vertical Layout */}
              <div className="flex flex-col gap-6">
                {/* Step 1 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-6 flex items-start gap-4 transition-all hover:bg-[#F5F5FF]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">Connect your Stripe account</h3>
                    <p className="text-base text-[#6B7280]"><span className="text-[#0070F3] font-semibold">KeepMore</span> sets up hosted checkout pages for you. No coding required.</p>
                  </div>
                </motion.div>
                
                {/* Step 2 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-6 flex items-start gap-4 transition-all hover:bg-[#F5F5FF]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">Customers see transparent options</h3>
                    <p className="text-base text-[#6B7280]">Card payments show the processing cost. Bank transfers have no added cost and are clearly labeled.</p>
                  </div>
                </motion.div>
                
                {/* Step 3 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] p-6 flex items-start gap-4 transition-all hover:bg-[#F5F5FF]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">You keep the full amount</h3>
                    <p className="text-base text-[#6B7280]">Each payment is automatically adjusted to cover processing costs so you never lose margin.</p>
                  </div>
                </motion.div>
              </div>

              {/* Invoice Preview */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0070F3]/10 to-[#0070F3]/5 rounded-3xl -rotate-2 transform"></div>
                <div className="relative bg-white border border-[#E5E7EB] rounded-2xl shadow-lg overflow-hidden">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <div className="h-6 w-24 rounded-md bg-[#0070F3]"></div>
                        <div className="h-3 w-32 rounded-md bg-[#E5E7EB] mt-2"></div>
                      </div>
                      <div className="bg-[#F5F7FF] text-[#0070F3] text-xs px-3 py-1 rounded-full font-medium">Invoice #1042</div>
                    </div>
                    
                    {/* Invoice Items */}
                    <div className="space-y-2 mb-6">
                      <div className="flex justify-between items-center pb-2 border-b border-[#E5E7EB]">
                        <p className="text-sm font-medium text-[#6B7280]">Description</p>
                        <p className="text-sm font-medium text-[#6B7280]">Amount</p>
                      </div>
                      <div className="flex justify-between text-[#111827]">
                        <p>Monthly Retainer</p>
                        <p>$3,500.00</p>
                      </div>
                      <div className="flex justify-between text-[#111827]">
                        <p>Additional Hours (5h)</p>
                        <p>$750.00</p>
                      </div>
                    </div>

                    <div className="border-t border-[#E5E7EB] pt-4 mb-6">
                      <div className="flex justify-between font-semibold text-lg text-[#111827]">
                        <p>Total Due</p>
                        <p>$4,250.00</p>
                      </div>
                    </div>
                    
                    {/* Payment Options */}
                    <div className="bg-white p-4 rounded-lg border border-[#E5E7EB]">
                      <p className="font-medium text-[#111827] mb-3">Select Payment Method</p>
                      
                      <div className="space-y-3">
                        <div className="bg-white border-2 border-[#0070F3] p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full border-2 border-[#0070F3] flex items-center justify-center">
                              <div className="w-2.5 h-2.5 rounded-full bg-[#0070F3]"></div>
                            </div>
                            <p className="font-medium text-[#111827]">Bank Transfer (ACH)</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#111827]">$4,250.00</p>
                            <p className="text-xs text-green-600 font-medium">Save $127.05</p>
                          </div>
                        </div>
                        
                        <div className="bg-white border border-[#E5E7EB] p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-5 h-5 rounded-full border-2 border-[#E5E7EB]"></div>
                            <p className="font-medium text-[#111827]">Credit Card</p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-[#111827]">$4,377.05</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>


      {/* Migration + How It Works Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
          >
            <h2 className="text-4xl font-bold text-[#000000] mb-6 text-center">
              Update existing subscriptions to cover processing costs.
            </h2>
            <p className="text-lg text-[#6B7280] mb-12 text-center max-w-[700px] mx-auto">
              No cancellations. No rebuilds. No downtime.
            </p>

            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto items-center">
              {/* Steps - Vertical Layout */}
              <div className="flex flex-col gap-6">
                {/* Step 1 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] border border-[#E5E7EB] p-6 flex items-start gap-4 transition-all hover:bg-[#F9F9F9]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">1</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">Securely connect to Stripe</h3>
                    <p className="text-base text-[#6B7280]">One-click OAuth connection. Setup takes five minutes and requires no code.</p>
                  </div>
                </motion.div>
                
                {/* Step 2 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] border border-[#E5E7EB] p-6 flex items-start gap-4 transition-all hover:bg-[#F9F9F9]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">2</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">Notify customers</h3>
                    <p className="text-base text-[#6B7280]"><span className="text-[#0070F3] font-semibold">KeepMore</span> emails the customers you select with their options.</p>
                  </div>
                </motion.div>
                
                {/* Step 3 */}
                <motion.div 
                  className="bg-white rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] border border-[#E5E7EB] p-6 flex items-start gap-4 transition-all hover:bg-[#F9F9F9]"
                  whileHover={{ x: 4 }}
                >
                  <div className="w-10 h-10 rounded-full bg-[#0070F3] flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-lg">3</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#111827] mb-1">Keep your full amount</h3>
                    <p className="text-base text-[#6B7280]">Every renewal includes the proper cost adjustment — you keep 100% of what you earned.</p>
                  </div>
                </motion.div>
              </div>

              {/* Email Preview */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0070F3]/10 to-[#0070F3]/5 rounded-3xl -rotate-2 transform"></div>
                <div className="relative bg-white border border-[#E5E7EB] rounded-2xl shadow-lg overflow-hidden">
                  {/* Email Header */}
                  <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-3 h-3 rounded-full bg-[#EF4444]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#F59E0B]"></div>
                      <div className="w-3 h-3 rounded-full bg-[#22C55E]"></div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-[#6B7280]"><span className="font-medium text-[#111827]">From:</span> billing@yourcompany.com</p>
                      <p className="text-xs text-[#6B7280]"><span className="font-medium text-[#111827]">Subject:</span> Update your payment method</p>
                    </div>
                  </div>
                  
                  {/* Email Body */}
                  <div className="p-6">
                    <p className="text-sm text-[#111827] mb-4">Hi Sarah,</p>
                    <p className="text-sm text-[#6B7280] mb-4">
                      Starting next month, your subscription will include card processing costs. 
                      You can avoid these costs by switching to bank transfer (ACH).
                    </p>
                    
                    <div className="bg-white p-4 rounded-lg border border-[#E5E7EB] mb-4">
                      <p className="font-medium text-[#111827] text-sm mb-3">Choose your preference:</p>
                      
                      <div className="space-y-3">
                        <div className="bg-white border-2 border-[#0070F3] p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full border-2 border-[#0070F3] flex items-center justify-center">
                              <div className="w-2 h-2 rounded-full bg-[#0070F3]"></div>
                            </div>
                            <div>
                              <p className="font-medium text-[#111827] text-sm">Switch to Bank Transfer</p>
                              <p className="text-xs text-[#6B7280]">No added cost</p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-green-600">$500/mo</p>
                        </div>
                        
                        <div className="bg-white border border-[#E5E7EB] p-3 rounded-lg flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-4 h-4 rounded-full border-2 border-[#E5E7EB]"></div>
                            <div>
                              <p className="font-medium text-[#111827] text-sm">Keep Credit Card</p>
                            </div>
                          </div>
                          <p className="text-sm font-semibold text-[#111827]">$514.80/mo</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex gap-3">
                      <div className="bg-[#0070F3] text-white text-sm font-medium px-4 py-2 rounded-md text-center">
                        Update Payment Method
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="max-w-[800px] mx-auto"
          >
            <h2 className="text-4xl font-bold text-[#000000] mb-6 text-center">
              Common Questions
            </h2>

            <Accordion type="single" collapsible className="w-full space-y-3">
              <AccordionItem value="item-1" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Does this work with my existing Stripe account?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  Yes. <span className="text-[#0070F3] font-semibold">KeepMore</span> connects directly to your current Stripe account. You don't need to switch processors or rebuild anything.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Will my customers notice a change?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  They'll just see payment options listed clearly. Card payments show the processing cost, and bank transfers are marked as no added cost.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Is this allowed by Stripe?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  Yes. <span className="text-[#0070F3] font-semibold">KeepMore</span> operates entirely within Stripe's system and follows their Terms of Service.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Do I need to change my pricing?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  No. <span className="text-[#0070F3] font-semibold">KeepMore</span> adjusts checkout amounts so you still receive your intended total after processing costs.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  How long does setup take?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  About five minutes. Connect your Stripe account and <span className="text-[#0070F3] font-semibold">KeepMore</span> handles the rest.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-6" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  What happens with existing subscriptions?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  They stay active. <span className="text-[#0070F3] font-semibold">KeepMore</span> updates renewals so customers cover the processing costs moving forward.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-7" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Can customers still pay by card?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  Yes. They can choose between a no-added-cost bank transfer or paying by card with the cost included.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-8" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Are bank transfers really free?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  Stripe charges 0.8% for bank transfers, capped at $5 per transaction.
                  That's far cheaper than paying 2.9% + $0.30 for cards.
                  For example, on a $5,000 payment, card processing costs about $145, while the bank transfer cost is only $5 — saving $140 on one payment.
                  That's why <span className="text-[#0070F3] font-semibold">KeepMore</span> labels bank transfers as "no added cost" for customers — it's close enough that you save significantly on every large invoice.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-9" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  Do I have to move all my customers at once?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left">
                  No. You're in control. You can decide which customers move to <span className="text-[#0070F3] font-semibold">KeepMore</span> and when.
                  This lets you roll it out gradually or only for certain clients while keeping everyone else on your existing billing setup.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-10" className="bg-white border border-[#E5E7EB] rounded-lg shadow-[0_2px_6px_rgba(0,0,0,0.05)] px-6 data-[state=open]:bg-[#F5F7FF]">
                <AccordionTrigger className="text-lg font-medium text-[#111827] py-4 hover:no-underline focus:outline-2 focus:outline-[#0070F3] text-left">
                  How does the pricing work?
                </AccordionTrigger>
                <AccordionContent className="text-base text-[#6B7280] pb-5 text-left space-y-4">
                  <p><span className="text-[#0070F3] font-semibold">KeepMore</span> is $39/month with a 30-day free trial. No contracts, cancel anytime.</p>
                  <p>When a customer chooses to pay by credit card, <span className="text-[#0070F3] font-semibold">KeepMore</span> automatically adds both Stripe's standard card rate (2.9% + $0.30) and <span className="text-[#0070F3] font-semibold">KeepMore</span>'s 1% to the checkout total. Those costs are paid by the customer and are processed as one single Stripe transaction, not separate charges.</p>
                  <p>If the customer pays by bank transfer, you only pay Stripe's 0.8% cost capped at $5.</p>
                  <p>You keep the full invoice amount you set — the $39/month subscription pays for itself after just a few transactions.</p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 mb-16">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="text-4xl font-bold text-[#000000] mb-6">
              Stop absorbing card processing costs on Stripe transactions.
            </h2>
            <p className="text-lg text-[#6B7280] mb-8">
              You earned the revenue. <span className="text-[#0070F3] font-semibold">KeepMore</span> of it.
            </p>
            <div className="flex flex-col gap-2 items-center">
              <Link to="/auth?mode=signup">
                <Button className="bg-[#0070F3] hover:bg-[#0059CC] text-white px-8 py-4 rounded-md text-lg">
                  Start Free Trial →
                </Button>
              </Link>
              <p className="text-sm text-[#6B7280]">(no credit card required)</p>
            </div>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Index;